import { useEffect, useMemo, useState } from 'react';
import {
  IconPlus, IconPencil, IconSearch, IconAlertCircle, IconCheck, IconX,
  IconChevronDown, IconChevronRight, IconCoffee, IconBeer, IconBowl, IconBread,
  IconCake, IconCookie, IconLeaf, IconSquareDot, IconSparkles, IconRuler,
  IconCirclePlus, IconCircleCheck, IconSettings, IconLeaf2,
  IconBoxSeam, IconLayoutGrid, IconResize, IconCoin, IconChartPie,
  IconBan, IconCircleOff,
} from '@tabler/icons-react';
import AdminLayout from '../../components/layout/AdminLayout';
import Modal from '../../components/ui/Modal';

/**
 * Phase 4.2 — Admin Products page (redesigned).
 *
 *   - Categories panel: rich tone-coloured cards with product counts.
 *   - Filters: search · category · diet · status (with counts).
 *   - Products list: expandable rows showing tone-coloured category tag,
 *     capability badges (sizes, add-ons, configurable…), diet, price,
 *     and an inline detail panel for the full configuration.
 *
 * Soft-deletes via isActive=false; orders that referenced retired
 * items keep their snapshotted name + price (Order.lines.name etc).
 */

interface ApiCategory {
  id: string;
  name: string;
  slug: string;
  tone: string;
  iconKey: string;
  sortOrder: number;
  isActive: boolean;
}

interface ApiProduct {
  id: string;
  categoryId: string;
  name: string;
  description: string | null;
  priceCents: number;
  originalPriceCents: number | null;
  diet: 'VEG' | 'NON_VEG' | 'EGG';
  badge: 'NONE' | 'TRENDING' | 'MUST_TRY' | 'HOT';
  available: number;
  imageUrl: string | null;
  barcode: string | null;
  requiresConfiguration: boolean;
  sizes: { name: string; priceDelta: number }[];
  defaultIngredients: string[];
  availableAddons: { name: string; priceCents: number }[];
  sortOrder: number;
  isActive: boolean;
}

type DietFilter = 'all' | 'VEG' | 'NON_VEG' | 'EGG';
type StatusFilter = 'all' | 'active' | 'inactive';

function fmtR(cents: number) {
  return `R ${(cents / 100).toFixed(2)}`;
}

function slugify(name: string) {
  return name.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

// Tone → Tailwind class map. Tailwind JIT needs literal strings, so we list
// every combination explicitly rather than building class names with template
// literals (which would get purged from the build).
const TONE_CLASSES = {
  primary:   { solid: 'bg-primary text-[#FFFFFF]',   soft: 'bg-primary-soft text-primary',     text: 'text-primary',   border: 'border-primary',   accent: 'bg-primary'   },
  secondary: { solid: 'bg-secondary text-[#FFFFFF]', soft: 'bg-secondary-soft text-secondary', text: 'text-secondary', border: 'border-secondary', accent: 'bg-secondary' },
  orange:    { solid: 'bg-orange text-[#FFFFFF]',    soft: 'bg-orange-soft text-orange',       text: 'text-orange',    border: 'border-orange',    accent: 'bg-orange'    },
  info:      { solid: 'bg-info text-[#FFFFFF]',      soft: 'bg-info-soft text-info',           text: 'text-info',      border: 'border-info',      accent: 'bg-info'      },
  success:   { solid: 'bg-success text-[#FFFFFF]',   soft: 'bg-success-soft text-success',     text: 'text-success',   border: 'border-success',   accent: 'bg-success'   },
  warning:   { solid: 'bg-warning text-[#FFFFFF]',   soft: 'bg-warning-soft text-warning',     text: 'text-warning',   border: 'border-warning',   accent: 'bg-warning'   },
  pink:      { solid: 'bg-pink text-[#FFFFFF]',      soft: 'bg-pink-soft text-pink',           text: 'text-pink',      border: 'border-pink',      accent: 'bg-pink'      },
  rose:      { solid: 'bg-rose text-[#FFFFFF]',      soft: 'bg-rose-soft text-rose',           text: 'text-rose',      border: 'border-rose',      accent: 'bg-rose'      },
  purple:    { solid: 'bg-purple text-[#FFFFFF]',    soft: 'bg-purple-soft text-purple',       text: 'text-purple',    border: 'border-purple',    accent: 'bg-purple'    },
  indigo:    { solid: 'bg-indigo text-[#FFFFFF]',    soft: 'bg-indigo-soft text-indigo',       text: 'text-indigo',    border: 'border-indigo',    accent: 'bg-indigo'    },
  teal:      { solid: 'bg-teal text-[#FFFFFF]',      soft: 'bg-teal-soft text-teal',           text: 'text-teal',      border: 'border-teal',      accent: 'bg-teal'      },
  lime:      { solid: 'bg-lime text-[#FFFFFF]',      soft: 'bg-lime-soft text-lime',           text: 'text-lime',      border: 'border-lime',      accent: 'bg-lime'      },
  danger:    { solid: 'bg-danger text-[#FFFFFF]',    soft: 'bg-danger-soft text-danger',       text: 'text-danger',    border: 'border-danger',    accent: 'bg-danger'    },
} as const;

type ToneKey = keyof typeof TONE_CLASSES;

function toneOf(t: string): ToneKey {
  return (t in TONE_CLASSES ? t : 'primary') as ToneKey;
}

const ICON_BY_KEY: Record<string, typeof IconSparkles> = {
  coffee: IconCoffee, beer: IconBeer, bowl: IconBowl, bread: IconBread,
  cake: IconCake, cookie: IconCookie, leaf: IconLeaf, sparkles: IconSparkles,
  circle: IconSquareDot,
};

function CategoryIcon({ iconKey, className, size = 22 }: { iconKey: string; className?: string; size?: number }) {
  const Icon = ICON_BY_KEY[iconKey] ?? IconSparkles;
  return <Icon size={size} stroke={2} className={className} />;
}

function DietDot({ diet }: { diet: ApiProduct['diet'] }) {
  const map = {
    VEG:     { label: 'Veg',     dot: 'bg-success', text: 'text-success' },
    NON_VEG: { label: 'Non veg', dot: 'bg-danger',  text: 'text-danger'  },
    EGG:     { label: 'Egg',     dot: 'bg-warning', text: 'text-warning' },
  } as const;
  const m = map[diet];
  return (
    <span className="inline-flex items-center gap-1.5 text-12 font-bold uppercase tracking-wider">
      <span className={`w-2 h-2 rounded-full ${m.dot}`} />
      <span className={m.text}>{m.label}</span>
    </span>
  );
}

export default function AdminProductsPage() {
  const [categories, setCategories] = useState<ApiCategory[]>([]);
  const [products, setProducts] = useState<ApiProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [dietFilter, setDietFilter] = useState<DietFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [editingCategory, setEditingCategory] = useState<ApiCategory | 'new' | null>(null);
  const [editingProduct, setEditingProduct] = useState<ApiProduct | 'new' | null>(null);

  async function load() {
    setLoading(true);
    try {
      const [c, p] = await Promise.all([
        fetch('/api/categories?includeInactive=1', { credentials: 'include' }),
        fetch('/api/products?includeInactive=1', { credentials: 'include' }),
      ]);
      if (!c.ok || !p.ok) throw new Error(`HTTP ${c.status}/${p.status}`);
      const cd = await c.json();
      const pd = await p.json();
      setCategories(cd.categories ?? []);
      setProducts(pd.products ?? []);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, []);

  const categoryById = useMemo(() => {
    const m = new Map<string, ApiCategory>();
    for (const c of categories) m.set(c.id, c);
    return m;
  }, [categories]);

  const countsByCategory = useMemo(() => {
    const m = new Map<string, { total: number; active: number }>();
    for (const c of categories) m.set(c.id, { total: 0, active: 0 });
    for (const p of products) {
      const row = m.get(p.categoryId);
      if (!row) continue;
      row.total++;
      if (p.isActive) row.active++;
    }
    return m;
  }, [products, categories]);

  const visibleProducts = useMemo(() => {
    const q = search.trim().toLowerCase();
    return products.filter((p) => {
      if (categoryFilter !== 'all' && p.categoryId !== categoryFilter) return false;
      if (dietFilter !== 'all' && p.diet !== dietFilter) return false;
      if (statusFilter === 'active' && !p.isActive) return false;
      if (statusFilter === 'inactive' && p.isActive) return false;
      if (q && !p.name.toLowerCase().includes(q) && !(p.description ?? '').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [products, categoryFilter, search, dietFilter, statusFilter]);

  const totalActive = products.filter((p) => p.isActive).length;

  const kpis = useMemo(() => {
    const active = products.filter((p) => p.isActive);
    const hidden = products.length - active.length;
    const activeCats = categories.filter((c) => c.isActive).length;
    const sizeVariants = active.reduce((n, p) => n + p.sizes.length, 0);
    const addonCount = active.reduce((n, p) => n + p.availableAddons.length, 0);
    const avgCents = active.length === 0
      ? 0
      : Math.round(active.reduce((n, p) => n + p.priceCents, 0) / active.length);
    const prices = active.map((p) => p.priceCents).filter((c) => c > 0);
    const minCents = prices.length ? Math.min(...prices) : 0;
    const maxCents = prices.length ? Math.max(...prices) : 0;
    const diet = { VEG: 0, NON_VEG: 0, EGG: 0 };
    for (const p of active) diet[p.diet]++;
    const dietTotal = active.length || 1;
    return {
      products: active.length,
      hidden,
      cats: activeCats,
      catsTotal: categories.length,
      sizeVariants,
      sizePerItem: active.length ? (sizeVariants / active.length).toFixed(1) : '0',
      addonCount,
      addonPerItem: active.length ? (addonCount / active.length).toFixed(1) : '0',
      avgCents,
      minCents,
      maxCents,
      vegPct:    Math.round((diet.VEG     / dietTotal) * 100),
      nonVegPct: Math.round((diet.NON_VEG / dietTotal) * 100),
      eggPct:    Math.round((diet.EGG     / dietTotal) * 100),
    };
  }, [products, categories]);

  async function toggleAvailability(p: ApiProduct) {
    const next = (p.available ?? 0) > 0 ? 0 : 99;
    // Optimistic local flip — server PATCH follows.
    setProducts((prev) => prev.map((x) => x.id === p.id ? { ...x, available: next } : x));
    try {
      const r = await fetch(`/api/products/${p.id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ available: next }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
    } catch (err) {
      // Revert on failure.
      setProducts((prev) => prev.map((x) => x.id === p.id ? { ...x, available: p.available } : x));
      setError((err as Error).message);
    }
  }

  function toggleExpand(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <AdminLayout
      title="Products"
      subtitle={`${totalActive} active · ${products.length} total · ${categories.filter((c) => c.isActive).length} categories`}
      actions={
        <button
          type="button"
          onClick={() => setEditingProduct('new')}
          disabled={categories.length === 0}
          className="h-10 px-4 inline-flex items-center gap-2 rounded-md bg-primary text-[#FFFFFF] text-13 font-bold hover:bg-primary-hover shadow-sm disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
        >
          <IconPlus size={16} stroke={2.5} />
          New product
        </button>
      }
    >
      {error && (
        <div className="rounded-md bg-danger-soft border border-danger/30 p-3 flex items-start gap-2 mb-5">
          <IconAlertCircle size={18} className="text-danger flex-shrink-0 mt-0.5" />
          <p className="text-13 font-semibold text-danger">{error}</p>
        </div>
      )}

      {/* KPI row */}
      {!loading && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
          <KpiTile tone="purple"  label="Total products"  value={kpis.products}                      Icon={IconBoxSeam} />
          <KpiTile tone="primary" label="Categories"      value={kpis.cats}                          Icon={IconLayoutGrid} />
          <KpiTile tone="orange"  label="Size variants"   value={kpis.sizeVariants}                  Icon={IconResize} />
          <KpiTile tone="success" label="Add-ons"         value={kpis.addonCount}                    Icon={IconSparkles} />
          <KpiTile tone="rose"    label="Average price"   value={fmtR(kpis.avgCents)}                Icon={IconCoin} />
          <KpiTile tone="info"    label="Veg of menu"     value={`${kpis.vegPct}%`}                  Icon={IconChartPie} />
        </div>
      )}

      {/* Categories */}
      <section className="rounded-lg bg-surface border border-border p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-15 font-bold text-heading">Categories</h2>
            <p className="text-12 font-medium text-gray-700 mt-0.5">
              {categories.length} total · click any card to edit
            </p>
          </div>
          <button
            type="button"
            onClick={() => setEditingCategory('new')}
            className="h-9 px-3 inline-flex items-center gap-1.5 rounded-md bg-light border-2 border-border text-12 font-bold uppercase tracking-wider text-heading hover:bg-primary hover:text-[#FFFFFF] hover:border-primary transition-colors"
          >
            <IconPlus size={14} stroke={2.5} />
            New category
          </button>
        </div>
        {loading ? (
          <p className="text-13 italic text-gray-700">Loading…</p>
        ) : categories.length === 0 ? (
          <p className="text-13 italic text-gray-700">No categories yet — create one to start adding products.</p>
        ) : (
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-3">
            {categories.map((c) => {
              const tone = toneOf(c.tone);
              const tc = TONE_CLASSES[tone];
              const counts = countsByCategory.get(c.id) ?? { total: 0, active: 0 };
              return (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => setEditingCategory(c)}
                    className={[
                      'group w-full text-left rounded-lg overflow-hidden transition-all',
                      c.isActive ? '' : 'opacity-60',
                      'hover:shadow-md hover:-translate-y-0.5',
                    ].join(' ')}
                  >
                    <div className={`${tc.soft} p-4 flex items-center gap-3`}>
                      <div className={`w-11 h-11 rounded-lg ${tc.solid} inline-flex items-center justify-center flex-shrink-0`}>
                        <CategoryIcon iconKey={c.iconKey} size={22} className="text-[#FFFFFF]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-15 font-bold text-heading truncate">{c.name}</p>
                          {!c.isActive && (
                            <span className="text-10 font-bold uppercase tracking-wider bg-light px-1.5 py-0.5 rounded text-gray-700 border border-border">Inactive</span>
                          )}
                        </div>
                        <p className="text-11 font-bold uppercase tracking-wider text-gray-700 mt-0.5 truncate">
                          {counts.active === counts.total
                            ? 'all active'
                            : `${counts.total - counts.active} inactive`}
                          <IconPencil
                            size={12}
                            stroke={2.25}
                            className="inline-block ml-1.5 -mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                          />
                        </p>
                      </div>
                      <div className="flex-shrink-0 text-right leading-none">
                        <p className={`text-28 font-bold tabular-nums ${tc.text}`}>
                          {counts.active}
                          {counts.active !== counts.total && (
                            <span className="text-15 text-gray-500 font-semibold">/{counts.total}</span>
                          )}
                        </p>
                        <p className="text-10 font-bold uppercase tracking-widest text-gray-700 mt-1">items</p>
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* Products */}
      <section className="rounded-lg bg-surface border border-border p-5">
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <div>
            <h2 className="text-15 font-bold text-heading">Products</h2>
            <p className="text-12 font-medium text-gray-700 mt-0.5">
              Showing {visibleProducts.length} of {products.length}
            </p>
          </div>
          <div className="flex-1" />
          <div className="relative">
            <IconSearch size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search products"
              className="h-10 w-60 pl-9 pr-3 rounded-md bg-light border-2 border-border focus:border-primary outline-none text-13 font-semibold text-heading"
            />
          </div>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="h-10 px-3 rounded-md bg-light border-2 border-border focus:border-primary outline-none text-13 font-semibold text-heading"
          >
            <option value="all">All categories</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <select
            value={dietFilter}
            onChange={(e) => setDietFilter(e.target.value as DietFilter)}
            className="h-10 px-3 rounded-md bg-light border-2 border-border focus:border-primary outline-none text-13 font-semibold text-heading"
          >
            <option value="all">All diets</option>
            <option value="VEG">Veg</option>
            <option value="NON_VEG">Non veg</option>
            <option value="EGG">Egg</option>
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            className="h-10 px-3 rounded-md bg-light border-2 border-border focus:border-primary outline-none text-13 font-semibold text-heading"
          >
            <option value="all">All status</option>
            <option value="active">Active only</option>
            <option value="inactive">Inactive only</option>
          </select>
        </div>

        {loading ? (
          <p className="text-13 italic text-gray-700">Loading…</p>
        ) : visibleProducts.length === 0 ? (
          <div className="text-center py-10 text-13 text-gray-700">
            {products.length === 0
              ? 'No products yet — tap "New product" once you have at least one category.'
              : 'No products match these filters.'}
          </div>
        ) : (
          <ul className="divide-y divide-border-soft">
            {visibleProducts.map((p) => {
              const cat = categoryById.get(p.categoryId);
              const tone = toneOf(cat?.tone ?? 'primary');
              const tc = TONE_CLASSES[tone];
              const isOpen = expanded.has(p.id);
              const sizeCount = p.sizes.length;
              const addonCount = p.availableAddons.length;
              const ingCount = p.defaultIngredients.length;
              return (
                <li key={p.id}>
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => toggleExpand(p.id)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleExpand(p.id); } }}
                    className="py-3 px-2 -mx-2 flex items-center gap-3 cursor-pointer rounded-md hover:bg-light/60 transition-colors"
                  >
                    {/* Chevron */}
                    <div className="w-6 h-6 inline-flex items-center justify-center text-gray-700 flex-shrink-0">
                      {isOpen ? <IconChevronDown size={16} stroke={2.5} /> : <IconChevronRight size={16} stroke={2.5} />}
                    </div>

                    {/* Category icon swatch */}
                    {cat && (
                      <div className={`w-10 h-10 rounded-md ${tc.solid} inline-flex items-center justify-center shadow-sm flex-shrink-0`}>
                        <CategoryIcon iconKey={cat.iconKey} size={18} className="text-[#FFFFFF]" />
                      </div>
                    )}

                    {/* Name + category tag + capability badges */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-14 font-bold text-heading">{p.name}</p>
                        {cat && (
                          <span className={`inline-flex items-center px-2 h-6 rounded-md ${tc.soft} text-11 font-bold uppercase tracking-wider`}>
                            {cat.name}
                          </span>
                        )}
                        {!p.isActive && (
                          <span className="inline-flex items-center px-2 h-6 rounded-md bg-light border border-border text-11 font-bold uppercase tracking-wider text-gray-700">
                            Inactive
                          </span>
                        )}
                        {p.isActive && p.available <= 0 && (
                          <span className="inline-flex items-center gap-1 px-2 h-6 rounded-md bg-danger text-[#FFFFFF] text-11 font-bold uppercase tracking-wider">
                            <IconCircleOff size={12} stroke={2.5} />
                            Sold out
                          </span>
                        )}
                      </div>
                      {/* Capability row */}
                      <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                        {sizeCount > 0 && (
                          <CapBadge tone="info" icon={<IconRuler size={12} stroke={2.5} />}>
                            {sizeCount} {sizeCount === 1 ? 'size' : 'sizes'}
                          </CapBadge>
                        )}
                        {addonCount > 0 && (
                          <CapBadge tone="purple" icon={<IconCirclePlus size={12} stroke={2.5} />}>
                            {addonCount} add-on{addonCount === 1 ? '' : 's'}
                          </CapBadge>
                        )}
                        {ingCount > 0 && (
                          <CapBadge tone="success" icon={<IconLeaf2 size={12} stroke={2.5} />}>
                            {ingCount} ingredient{ingCount === 1 ? '' : 's'}
                          </CapBadge>
                        )}
                        {p.requiresConfiguration && (
                          <CapBadge tone="danger" icon={<IconSettings size={12} stroke={2.5} />}>
                            Configurable
                          </CapBadge>
                        )}
                        {p.badge !== 'NONE' && (
                          <CapBadge tone="warning" icon={<IconCircleCheck size={12} stroke={2.5} />}>
                            {p.badge.replace('_', ' ').toLowerCase()}
                          </CapBadge>
                        )}
                      </div>
                    </div>

                    {/* Diet */}
                    <div className="hidden sm:block flex-shrink-0 w-24">
                      <DietDot diet={p.diet} />
                    </div>

                    {/* Price */}
                    <div className="flex-shrink-0 text-right">
                      <p className="text-15 font-bold text-heading tabular-nums">{fmtR(p.priceCents)}</p>
                      {p.originalPriceCents != null && (
                        <p className="text-11 text-gray-500 line-through tabular-nums">{fmtR(p.originalPriceCents)}</p>
                      )}
                    </div>

                    {/* 86 / Restore toggle */}
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); void toggleAvailability(p); }}
                      className={[
                        'w-9 h-9 inline-flex items-center justify-center rounded-md border-2 flex-shrink-0 transition-colors',
                        p.available <= 0
                          ? 'border-success text-success hover:bg-success hover:text-[#FFFFFF]'
                          : 'border-danger  text-danger  hover:bg-danger  hover:text-[#FFFFFF]',
                      ].join(' ')}
                      title={p.available <= 0 ? 'Restore — make available' : '86 — mark unavailable'}
                    >
                      {p.available <= 0 ? <IconCircleCheck size={14} stroke={2.25} /> : <IconBan size={14} stroke={2.25} />}
                    </button>

                    {/* Edit button */}
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setEditingProduct(p); }}
                      className="w-9 h-9 inline-flex items-center justify-center rounded-md border-2 border-border text-gray-700 hover:bg-primary hover:text-[#FFFFFF] hover:border-primary flex-shrink-0 transition-colors"
                      title="Edit product"
                    >
                      <IconPencil size={14} stroke={2.25} />
                    </button>
                  </div>

                  {/* Expanded detail */}
                  {isOpen && (
                    <div className="ml-9 mb-3 -mt-1 rounded-md bg-light border border-border-soft p-4 space-y-4">
                      {p.description && (
                        <div>
                          <DetailLabel>Description</DetailLabel>
                          <p className="text-13 font-medium text-heading">{p.description}</p>
                        </div>
                      )}
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                        <div>
                          <DetailLabel>Sizes ({sizeCount})</DetailLabel>
                          {sizeCount === 0 ? (
                            <p className="text-12 italic text-gray-600">No size options.</p>
                          ) : (
                            <div className="flex flex-wrap gap-1.5">
                              {p.sizes.map((s) => (
                                <span key={s.name} className="inline-flex items-center gap-1.5 px-2.5 h-7 rounded-md bg-info-soft text-12 font-bold">
                                  <span className="text-heading">{s.name}</span>
                                  <span className="text-gray-700">·</span>
                                  <span className="tabular-nums text-info">
                                    {s.priceDelta === 0 ? 'base' : `${s.priceDelta > 0 ? '+' : ''}${fmtR(s.priceDelta)}`}
                                  </span>
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        <div>
                          <DetailLabel>Default ingredients ({ingCount})</DetailLabel>
                          {ingCount === 0 ? (
                            <p className="text-12 italic text-gray-600">None set.</p>
                          ) : (
                            <div className="flex flex-wrap gap-1.5">
                              {p.defaultIngredients.map((ing) => (
                                <span key={ing} className="inline-flex items-center px-2.5 h-7 rounded-md bg-success-soft text-12 font-bold text-success">
                                  {ing}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        <div>
                          <DetailLabel>Add-ons ({addonCount})</DetailLabel>
                          {addonCount === 0 ? (
                            <p className="text-12 italic text-gray-600">No add-ons.</p>
                          ) : (
                            <div className="flex flex-wrap gap-1.5">
                              {p.availableAddons.map((a) => (
                                <span key={a.name} className="inline-flex items-center gap-1.5 px-2.5 h-7 rounded-md bg-purple-soft text-12 font-bold">
                                  <span className="text-heading">{a.name}</span>
                                  <span className="text-gray-700">·</span>
                                  <span className="tabular-nums text-purple">+{fmtR(a.priceCents)}</span>
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* Modals */}
      <CategoryFormModal
        target={editingCategory}
        onClose={() => setEditingCategory(null)}
        onSaved={() => { void load(); setEditingCategory(null); }}
      />
      <ProductFormModal
        target={editingProduct}
        categories={categories}
        onClose={() => setEditingProduct(null)}
        onSaved={() => { void load(); setEditingProduct(null); }}
      />
    </AdminLayout>
  );
}

/**
 * KPI tile — minimal stat block.
 *   Big bold count · short label below · tone-coloured soft icon chip on the right.
 *   No halo, no corner sweep, no pill — the colour + icon do the work.
 */
function KpiTile({
  tone, label, value, Icon,
}: {
  tone: ToneKey;
  label: string;
  value: number | string;
  Icon: typeof IconSparkles;
}) {
  const tc = TONE_CLASSES[tone];
  return (
    <div className={`rounded-lg ${tc.soft} p-4 flex items-center justify-between gap-3 hover:shadow-md transition-shadow`}>
      <div className="flex-1 min-w-0">
        <p className="text-24 font-bold text-heading tabular-nums leading-none truncate">{value}</p>
        <p className="text-13 font-semibold text-gray-700 mt-1 truncate">{label}</p>
      </div>
      <div className={`w-10 h-10 rounded-lg ${tc.solid} inline-flex items-center justify-center flex-shrink-0`}>
        <Icon size={20} stroke={2.25} className="text-[#FFFFFF]" />
      </div>
    </div>
  );
}

function CapBadge({ tone, icon, children }: { tone: ToneKey; icon: React.ReactNode; children: React.ReactNode }) {
  const tc = TONE_CLASSES[tone];
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 h-5 rounded ${tc.soft} text-11 font-bold capitalize`}>
      {icon}
      {children}
    </span>
  );
}

function DetailLabel({ children }: { children: React.ReactNode }) {
  return <p className="text-11 font-bold uppercase tracking-wider text-gray-700 mb-1.5">{children}</p>;
}

// ---------------------------------------------------------------------------
// CategoryFormModal
// ---------------------------------------------------------------------------

const CATEGORY_TONES: ToneKey[] = ['primary', 'secondary', 'orange', 'info', 'success', 'warning', 'pink', 'rose', 'purple', 'indigo', 'teal', 'lime', 'danger'];
const CATEGORY_ICON_KEYS = ['coffee', 'beer', 'bowl', 'bread', 'cake', 'cookie', 'leaf', 'sparkles', 'circle'];

function CategoryFormModal({
  target, onClose, onSaved,
}: {
  target: ApiCategory | 'new' | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = target === 'new';
  const existing = target && target !== 'new' ? target : null;
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [tone, setTone] = useState<ToneKey>('primary');
  const [iconKey, setIconKey] = useState('circle');
  const [sortOrder, setSortOrder] = useState(100);
  const [isActive, setIsActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!target) return;
    if (target === 'new') {
      setName(''); setSlug(''); setTone('primary'); setIconKey('circle'); setSortOrder(100); setIsActive(true);
    } else {
      setName(target.name); setSlug(target.slug); setTone(toneOf(target.tone)); setIconKey(target.iconKey);
      setSortOrder(target.sortOrder); setIsActive(target.isActive);
    }
    setErr(null);
  }, [target]);

  useEffect(() => {
    if (isNew && name) setSlug(slugify(name));
  }, [name, isNew]);

  async function save() {
    setSaving(true);
    setErr(null);
    try {
      const url = isNew ? '/api/categories' : `/api/categories/${existing!.id}`;
      const method = isNew ? 'POST' : 'PATCH';
      const r = await fetch(url, {
        method,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, slug, tone, iconKey, sortOrder, isActive }),
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        throw new Error(data.error ?? `HTTP ${r.status}`);
      }
      onSaved();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal open={target !== null} onClose={onClose} size="md" title={isNew ? 'New category' : `Edit · ${existing?.name ?? ''}`}>
      <div className="space-y-4">
        <Field label="Name" required>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Hot drinks"
            className="w-full h-11 px-3 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-14 font-semibold text-heading"
          />
        </Field>
        <Field label="Slug" helper="URL-safe identifier — auto-filled from name">
          <input
            type="text"
            value={slug}
            onChange={(e) => setSlug(e.target.value.replace(/[^a-z0-9-]/g, ''))}
            placeholder="hot-drinks"
            className="w-full h-11 px-3 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-14 font-mono text-heading"
          />
        </Field>

        <Field label="Tone" helper="Card colour in the admin + POS catalogue">
          <div className="grid grid-cols-[repeat(13,minmax(0,1fr))] gap-1.5">
            {CATEGORY_TONES.map((t) => {
              const tc = TONE_CLASSES[t];
              const selected = tone === t;
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTone(t)}
                  title={t}
                  className={[
                    'h-10 rounded-md border-2 transition-all',
                    tc.accent,
                    selected ? 'border-heading scale-110 shadow-md' : 'border-transparent opacity-80 hover:opacity-100',
                  ].join(' ')}
                />
              );
            })}
          </div>
        </Field>

        <Field label="Icon" helper="Shown on the category card + POS catalogue tile">
          <div className="grid grid-cols-9 gap-2">
            {CATEGORY_ICON_KEYS.map((k) => {
              const selected = iconKey === k;
              const tc = TONE_CLASSES[tone];
              return (
                <button
                  key={k}
                  type="button"
                  onClick={() => setIconKey(k)}
                  title={k}
                  className={[
                    'h-10 inline-flex items-center justify-center rounded-md border-2 transition-colors',
                    selected ? `${tc.solid} ${tc.border}` : 'bg-light border-border text-heading hover:border-heading',
                  ].join(' ')}
                >
                  <CategoryIcon iconKey={k} size={18} />
                </button>
              );
            })}
          </div>
        </Field>

        <Field label="Sort order">
          <input
            type="number"
            value={sortOrder}
            onChange={(e) => setSortOrder(Number(e.target.value) || 0)}
            className="w-full h-11 px-3 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-14 font-semibold tabular-nums text-heading"
          />
        </Field>
        <label className="flex items-center gap-2.5 cursor-pointer p-3 rounded-md bg-light border border-border-soft">
          <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} className="w-4 h-4" />
          <span>
            <span className="text-14 font-bold text-heading block">Active</span>
            <span className="text-12 font-medium text-gray-700 block">Shown in the POS catalogue</span>
          </span>
        </label>

        {err && <p className="text-13 font-semibold text-danger">{err}</p>}

        <div className="flex items-stretch gap-2 pt-2">
          <button onClick={onClose} disabled={saving} className="flex-1 h-12 rounded-md bg-light border-2 border-border text-14 font-bold text-heading hover:bg-gray-100">Cancel</button>
          <button
            onClick={save}
            disabled={saving || !name.trim() || !slug.trim()}
            className="flex-[2] h-12 inline-flex items-center justify-center gap-2 rounded-md bg-primary text-[#FFFFFF] text-14 font-bold hover:bg-primary-hover disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            <IconCheck size={16} stroke={2.5} />
            {saving ? 'Saving…' : (isNew ? 'Create category' : 'Save changes')}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// ProductFormModal — unchanged from prior version
// ---------------------------------------------------------------------------

function ProductFormModal({
  target, categories, onClose, onSaved,
}: {
  target: ApiProduct | 'new' | null;
  categories: ApiCategory[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = target === 'new';
  const existing = target && target !== 'new' ? target : null;

  const [name, setName] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [description, setDescription] = useState('');
  const [priceRand, setPriceRand] = useState('');
  const [originalPriceRand, setOriginalPriceRand] = useState('');
  const [diet, setDiet] = useState<'VEG' | 'NON_VEG' | 'EGG'>('VEG');
  const [badge, setBadge] = useState<'NONE' | 'TRENDING' | 'MUST_TRY' | 'HOT'>('NONE');
  const [imageUrl, setImageUrl] = useState('');
  const [barcode, setBarcode] = useState('');
  const [requiresConfiguration, setRequiresConfiguration] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [sortOrder, setSortOrder] = useState(100);
  const [available, setAvailable] = useState(99);
  const [sizes, setSizes] = useState<{ name: string; priceDeltaRand: string }[]>([]);
  const [ingredients, setIngredients] = useState<string[]>([]);
  const [ingredientInput, setIngredientInput] = useState('');
  const [addons, setAddons] = useState<{ name: string; priceRand: string }[]>([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!target) return;
    if (target === 'new') {
      setName(''); setCategoryId(categories[0]?.id ?? '');
      setDescription(''); setPriceRand(''); setOriginalPriceRand('');
      setDiet('VEG'); setBadge('NONE'); setImageUrl(''); setBarcode('');
      setRequiresConfiguration(false); setIsActive(true); setSortOrder(100); setAvailable(99);
      setSizes([]); setIngredients([]); setIngredientInput(''); setAddons([]);
    } else {
      setName(target.name);
      setCategoryId(target.categoryId);
      setDescription(target.description ?? '');
      setPriceRand((target.priceCents / 100).toFixed(2));
      setOriginalPriceRand(target.originalPriceCents != null ? (target.originalPriceCents / 100).toFixed(2) : '');
      setDiet(target.diet); setBadge(target.badge);
      setImageUrl(target.imageUrl ?? ''); setBarcode(target.barcode ?? '');
      setRequiresConfiguration(target.requiresConfiguration);
      setIsActive(target.isActive); setSortOrder(target.sortOrder); setAvailable(target.available);
      setSizes(target.sizes.map((s) => ({ name: s.name, priceDeltaRand: (s.priceDelta / 100).toFixed(2) })));
      setIngredients(target.defaultIngredients); setIngredientInput('');
      setAddons(target.availableAddons.map((a) => ({ name: a.name, priceRand: (a.priceCents / 100).toFixed(2) })));
    }
    setErr(null);
  }, [target, categories]);

  function addIngredient() {
    const v = ingredientInput.trim();
    if (!v || ingredients.includes(v)) return;
    setIngredients([...ingredients, v]);
    setIngredientInput('');
  }

  async function save() {
    setSaving(true);
    setErr(null);
    try {
      const priceCents = Math.round(Number(priceRand.replace(',', '.')) * 100);
      if (!isFinite(priceCents) || priceCents < 0) throw new Error('Price is invalid');
      const originalPriceCents = originalPriceRand.trim()
        ? Math.round(Number(originalPriceRand.replace(',', '.')) * 100)
        : null;
      const body = {
        categoryId,
        name,
        description: description || null,
        priceCents,
        originalPriceCents,
        diet,
        badge,
        available,
        imageUrl: imageUrl || null,
        barcode: barcode || null,
        requiresConfiguration,
        sizes: sizes
          .filter((s) => s.name.trim())
          .map((s) => ({ name: s.name.trim(), priceDelta: Math.round(Number(s.priceDeltaRand.replace(',', '.')) * 100) || 0 })),
        defaultIngredients: ingredients,
        availableAddons: addons
          .filter((a) => a.name.trim())
          .map((a) => ({ name: a.name.trim(), priceCents: Math.max(0, Math.round(Number(a.priceRand.replace(',', '.')) * 100) || 0) })),
        sortOrder,
        isActive,
      };
      const url = isNew ? '/api/products' : `/api/products/${existing!.id}`;
      const method = isNew ? 'POST' : 'PATCH';
      const r = await fetch(url, {
        method,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        throw new Error(data.error ?? `HTTP ${r.status}`);
      }
      onSaved();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal open={target !== null} onClose={onClose} size="xl" title={isNew ? 'New product' : `Edit · ${existing?.name ?? ''}`}>
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Name" required>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Flat White"
              className="w-full h-11 px-3 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-14 font-semibold text-heading"
            />
          </Field>
          <Field label="Category" required>
            <select
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              className="w-full h-11 px-3 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-14 font-semibold text-heading"
            >
              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
        </div>
        <Field label="Description" helper="Shown on the product card">
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g. Smooth espresso · steamed milk"
            className="w-full h-11 px-3 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-14 font-semibold text-heading"
          />
        </Field>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Field label="Price (R)" required>
            <input
              type="text"
              inputMode="decimal"
              value={priceRand}
              onChange={(e) => setPriceRand(e.target.value.replace(/[^0-9.,]/g, ''))}
              placeholder="32.00"
              className="w-full h-11 px-3 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-14 font-bold tabular-nums text-heading"
            />
          </Field>
          <Field label="Was-price (R)" helper="optional · strike-through">
            <input
              type="text"
              inputMode="decimal"
              value={originalPriceRand}
              onChange={(e) => setOriginalPriceRand(e.target.value.replace(/[^0-9.,]/g, ''))}
              placeholder=""
              className="w-full h-11 px-3 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-14 font-semibold tabular-nums text-heading"
            />
          </Field>
          <Field label="Available qty">
            <input
              type="number"
              value={available}
              onChange={(e) => setAvailable(Math.max(0, Number(e.target.value) || 0))}
              className="w-full h-11 px-3 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-14 font-semibold tabular-nums text-heading"
            />
          </Field>
          <Field label="Sort order">
            <input
              type="number"
              value={sortOrder}
              onChange={(e) => setSortOrder(Number(e.target.value) || 0)}
              className="w-full h-11 px-3 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-14 font-semibold tabular-nums text-heading"
            />
          </Field>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Field label="Diet">
            <select value={diet} onChange={(e) => setDiet(e.target.value as 'VEG' | 'NON_VEG' | 'EGG')} className="w-full h-11 px-3 rounded-md bg-surface border-2 border-border outline-none text-14 font-semibold text-heading">
              <option value="VEG">Veg</option>
              <option value="NON_VEG">Non veg</option>
              <option value="EGG">Egg</option>
            </select>
          </Field>
          <Field label="Badge">
            <select value={badge} onChange={(e) => setBadge(e.target.value as 'NONE' | 'TRENDING' | 'MUST_TRY' | 'HOT')} className="w-full h-11 px-3 rounded-md bg-surface border-2 border-border outline-none text-14 font-semibold text-heading">
              <option value="NONE">None</option>
              <option value="TRENDING">Trending</option>
              <option value="MUST_TRY">Must try</option>
              <option value="HOT">Hot</option>
            </select>
          </Field>
          <Field label="Image URL">
            <input
              type="url"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              placeholder="https://…"
              className="w-full h-11 px-3 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-14 font-semibold text-heading"
            />
          </Field>
          <Field label="Barcode">
            <input
              type="text"
              value={barcode}
              onChange={(e) => setBarcode(e.target.value)}
              placeholder=""
              className="w-full h-11 px-3 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-14 font-mono text-heading"
            />
          </Field>
        </div>

        <Field label="Size options" helper="Each adds its delta to the base price">
          <div className="space-y-2">
            {sizes.map((s, i) => (
              <div key={i} className="flex items-center gap-2">
                <input
                  type="text"
                  value={s.name}
                  onChange={(e) => setSizes(sizes.map((x, j) => j === i ? { ...x, name: e.target.value } : x))}
                  placeholder="Size name (e.g. Regular)"
                  className="flex-1 h-10 px-3 rounded-md bg-surface border-2 border-border outline-none text-13 font-semibold text-heading"
                />
                <input
                  type="text"
                  inputMode="decimal"
                  value={s.priceDeltaRand}
                  onChange={(e) => setSizes(sizes.map((x, j) => j === i ? { ...x, priceDeltaRand: e.target.value.replace(/[^0-9.,-]/g, '') } : x))}
                  placeholder="+0.00"
                  className="w-28 h-10 px-3 rounded-md bg-surface border-2 border-border outline-none text-13 font-bold tabular-nums text-heading"
                />
                <button
                  type="button"
                  onClick={() => setSizes(sizes.filter((_, j) => j !== i))}
                  className="w-9 h-10 inline-flex items-center justify-center rounded-md text-gray-700 hover:bg-danger-soft hover:text-danger"
                  title="Remove size"
                >
                  <IconX size={16} stroke={2.25} />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => setSizes([...sizes, { name: '', priceDeltaRand: '0' }])}
              className="h-9 px-3 inline-flex items-center gap-1.5 rounded-md bg-light border-2 border-border text-12 font-bold text-heading hover:bg-gray-100"
            >
              <IconPlus size={14} stroke={2.5} /> Add size
            </button>
          </div>
        </Field>

        <Field label="Default ingredients" helper="What's IN by default — cashier can untick to exclude">
          <div>
            <div className="flex flex-wrap gap-2 mb-2">
              {ingredients.map((ing) => (
                <span key={ing} className="inline-flex items-center gap-1 px-2 h-8 rounded-md bg-light border border-border text-12 font-bold text-heading">
                  {ing}
                  <button onClick={() => setIngredients(ingredients.filter((x) => x !== ing))} className="text-gray-700 hover:text-danger">
                    <IconX size={12} stroke={2.5} />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={ingredientInput}
                onChange={(e) => setIngredientInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addIngredient(); } }}
                placeholder="Type an ingredient and press Enter"
                className="flex-1 h-10 px-3 rounded-md bg-surface border-2 border-border outline-none text-13 font-semibold text-heading"
              />
              <button onClick={addIngredient} type="button" className="h-10 px-3 inline-flex items-center rounded-md bg-light border-2 border-border text-12 font-bold text-heading">
                Add
              </button>
            </div>
          </div>
        </Field>

        <Field label="Add-ons" helper="Optional extras the customer can choose">
          <div className="space-y-2">
            {addons.map((a, i) => (
              <div key={i} className="flex items-center gap-2">
                <input
                  type="text"
                  value={a.name}
                  onChange={(e) => setAddons(addons.map((x, j) => j === i ? { ...x, name: e.target.value } : x))}
                  placeholder="Add-on name (e.g. Extra shot)"
                  className="flex-1 h-10 px-3 rounded-md bg-surface border-2 border-border outline-none text-13 font-semibold text-heading"
                />
                <input
                  type="text"
                  inputMode="decimal"
                  value={a.priceRand}
                  onChange={(e) => setAddons(addons.map((x, j) => j === i ? { ...x, priceRand: e.target.value.replace(/[^0-9.,]/g, '') } : x))}
                  placeholder="6.00"
                  className="w-28 h-10 px-3 rounded-md bg-surface border-2 border-border outline-none text-13 font-bold tabular-nums text-heading"
                />
                <button
                  type="button"
                  onClick={() => setAddons(addons.filter((_, j) => j !== i))}
                  className="w-9 h-10 inline-flex items-center justify-center rounded-md text-gray-700 hover:bg-danger-soft hover:text-danger"
                  title="Remove add-on"
                >
                  <IconX size={16} stroke={2.25} />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => setAddons([...addons, { name: '', priceRand: '0' }])}
              className="h-9 px-3 inline-flex items-center gap-1.5 rounded-md bg-light border-2 border-border text-12 font-bold text-heading hover:bg-gray-100"
            >
              <IconPlus size={14} stroke={2.5} /> Add add-on
            </button>
          </div>
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <label className="flex items-center gap-2.5 cursor-pointer p-3 rounded-md bg-light border border-border-soft">
            <input type="checkbox" checked={requiresConfiguration} onChange={(e) => setRequiresConfiguration(e.target.checked)} className="w-4 h-4" />
            <span>
              <span className="text-14 font-bold text-heading block">Requires configuration</span>
              <span className="text-12 font-medium text-gray-700 block">Disable quick-add; force the configurator</span>
            </span>
          </label>
          <label className="flex items-center gap-2.5 cursor-pointer p-3 rounded-md bg-light border border-border-soft">
            <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} className="w-4 h-4" />
            <span>
              <span className="text-14 font-bold text-heading block">Active</span>
              <span className="text-12 font-medium text-gray-700 block">Shown in the POS catalogue</span>
            </span>
          </label>
        </div>

        {err && <p className="text-13 font-semibold text-danger">{err}</p>}

        <div className="flex items-stretch gap-2 pt-2">
          <button onClick={onClose} disabled={saving} className="flex-1 h-12 rounded-md bg-light border-2 border-border text-14 font-bold text-heading hover:bg-gray-100">Cancel</button>
          <button
            onClick={save}
            disabled={saving || !name.trim() || !categoryId || !priceRand.trim()}
            className="flex-[2] h-12 inline-flex items-center justify-center gap-2 rounded-md bg-primary text-[#FFFFFF] text-14 font-bold hover:bg-primary-hover disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            <IconCheck size={16} stroke={2.5} />
            {saving ? 'Saving…' : (isNew ? 'Create product' : 'Save changes')}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function Field({ label, helper, required, children }: { label: string; helper?: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-12 font-bold uppercase tracking-wider text-gray-700 mb-1.5">
        {label}{required && <span className="text-danger ml-1">*</span>}
        {helper && <span className="ml-2 text-gray-600 normal-case tracking-normal font-medium">— {helper}</span>}
      </span>
      {children}
    </label>
  );
}
